import { Alert, Box, Button, Container, Paper, Skeleton, Stack, Tooltip, Typography } from '@mui/material'
import { WarningAmber } from '@mui/icons-material'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate, useSearchParams } from 'react-router-dom'
import RoutesEnum from '../../components/Routes/rootEnum'
import SpinnerBlock from '../../components/Spinner/SpinnerBlock'
import { loginRequest } from '../../authConfig'
import { toSafeInternalPath } from './authRedirect'
import { getPublicBranding, defaultBranding, PublicBranding } from './brandingApi'
import { getCachedIdentityConfig } from './IdentityConfigService'
import messages from './loginMessages'
// Same platform brand assets the public /signup flow uses (blob-hosted), so Sign In matches signup.
import { PLATFORM_BACKGROUND_URL, PLATFORM_LOGO_URL } from '../Signup/platformAssets'
// Bundled ultimate fallback: unlike the blob-hosted URLs above it ships in the SPA bundle, so it can
// never 404 (env var unset / blob unreachable). Guarantees the card always shows *a* logo.
import defaultLogo from '../../assets/logo.png'

// E8 F8-1 (stories 8.1.1 / 8.1.3): the explicit, branded sign-in landing at /login — the canonical entry
// point since the PR6 cutover removed App's implicit useMsalAuthentication auto-redirect (AppShell now
// routes an unauthenticated user here). The page composes the per-Tenant identity-config (already fetched
// + cached by IdentityConfigLoader at boot) for the workspace display name and the local-vs-federation
// hints; the button hands off to Entra via MSAL.
const LoginPage = () => {
  const { formatMessage } = useIntl()
  const { instance } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const config = getCachedIdentityConfig()
  const workspaceName = config?.displayName ?? ''
  // Set by AuthCallbackPage when an interactive sign-in failed (?error) — surfaced as an inline alert below.
  const signInFailed = searchParams.get('error') !== null

  // 8.1.3 branding hydration. Start from the LogikSpark default so the card is styled immediately, then
  // swap in the Tenant's branding once GET /v1/branding answers. brandingReady gates only the logo
  // skeleton — the form fields render straight away and never remount when branding swaps in.
  const [branding, setBranding] = useState<PublicBranding>(defaultBranding)
  const [brandingReady, setBrandingReady] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    // 500ms ceiling: if the fetch hasn't answered, stop skeletoning and show the default — sign-in is
    // never blocked on branding.
    const ceiling = setTimeout(() => setBrandingReady(true), 500)

    getPublicBranding(controller.signal)
      .then((b) => {
        setBranding(b)
        setBrandingReady(true)
      })
      .catch(() => {
        // Slow/failed/404 (unknown subdomain) — keep the default; the page must still render + sign in.
        setBrandingReady(true)
      })

    return () => {
      controller.abort()
      clearTimeout(ceiling)
    }
  }, [])

  const displayName = branding.tenantDisplayName || workspaceName
  // Capture the title on mount and restore it on unmount, so the login title doesn't leak onto the next
  // page — the authed shell's usePageTitle only re-titles once it mounts, and public routes may not at all.
  useEffect(() => {
    const previousTitle = document.title
    return () => {
      document.title = previousTitle
    }
  }, [])
  useEffect(() => {
    document.title = formatMessage(messages.documentTitle, {
      workspace: displayName || 'LogikSpark',
    })
  }, [displayName, formatMessage])

  // An already-signed-in user should never sit on the login page (e.g. they navigated here manually, or
  // AppShell redirected here mid-interaction and the account resolved). Honour ?returnTo (origin-validated)
  // so a deep link still lands, else the dashboard.
  useEffect(() => {
    if (isAuthenticated) {
      navigate(toSafeInternalPath(searchParams.get('returnTo')) ?? RoutesEnum.DASHBOARD, {
        replace: true,
      })
    }
  }, [isAuthenticated, navigate, searchParams])

  // Already authenticated: the effect above is navigating away — render the spinner (not the sign-in card)
  // so the card never flashes for one frame on a manual/bookmark visit.
  if (isAuthenticated) {
    return <SpinnerBlock />
  }

  // domainHint routes the sign-in to a specific federation provider (E12 F12-7): External ID has no native
  // email-domain HRD, so the SPA passes the provider's domain_hint at /authorize. Omitted → the workspace's
  // own (local / default) sign-in.
  const handleSignIn = (domainHint?: string) => {
    // Where to land after the redirect completes (AuthCallbackPage reads this back off the MSAL `state`).
    // Honour a ?returnTo deep link, but only after origin-validating it to a same-origin relative path —
    // an unvalidated value would be an open redirect. Empty/cross-origin falls back to the dashboard.
    const state = toSafeInternalPath(searchParams.get('returnTo')) ?? RoutesEnum.DASHBOARD

    instance.loginRedirect({
      scopes: loginRequest.scopes,
      extraQueryParameters: domainHint
        ? { ...loginRequest.extraQueryParameters, domain_hint: domainHint }
        : loginRequest.extraQueryParameters,
      state,
    })
  }

  const federationProviders = config?.federationProviders ?? []
  // Hide the local sign-in button only for a federation-only workspace that actually has providers to route
  // to — otherwise (local allowed, or no providers configured) keep it so the user is never stranded.
  const showLocalSignIn = config?.localAccountsAllowed !== false || federationProviders.length === 0

  return (
    // Full-bleed platform background, matching the public /signup page.
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        backgroundImage: `url(${PLATFORM_BACKGROUND_URL})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <Container maxWidth='sm' sx={{ py: 8 }}>
        <Paper elevation={2} sx={{ p: 4 }}>
          <Stack spacing={3} alignItems='center'>
            {/* Logo: skeleton until branding resolves (or the 500ms ceiling), then the Tenant logo when one
                is set, else the LogikSpark platform logo (same blob asset as /signup). onError swaps once to
                the bundled logo — a can't-404 backstop — so a dead tenant/blob URL neither loops nor leaves
                the card logo-less. */}
            {brandingReady ? (
              <Box
                component='img'
                src={branding.logoUrl ?? PLATFORM_LOGO_URL}
                alt={formatMessage(messages.logoAlt, { workspace: displayName || 'LogikSpark' })}
                onError={(e) => {
                  // Swap to the bundled logo at most once. The flag stops the browser re-firing onError on
                  // a still-broken URL (the primary src IS the remote platform logo in the common no-tenant-
                  // logo case, so an unconditional re-assign to a remote URL could loop indefinitely).
                  const img = e.currentTarget as HTMLImageElement
                  if (img.dataset.fallbackApplied) {
                    return
                  }
                  img.dataset.fallbackApplied = 'true'
                  img.src = defaultLogo
                }}
                sx={{ maxHeight: 64, maxWidth: '100%', objectFit: 'contain' }}
              />
            ) : (
              <Skeleton variant='rectangular' width={180} height={64} />
            )}

            <Typography variant='h5' component='h1' textAlign='center'>
              {displayName
                ? formatMessage(messages.titleWithWorkspace, { workspace: displayName })
                : formatMessage(messages.title)}
            </Typography>

            <Typography variant='body2' color='text.secondary' textAlign='center'>
              {formatMessage(messages.subtitle)}
            </Typography>

            {signInFailed && (
              <Alert severity='error' sx={{ width: '100%' }}>
                {formatMessage(messages.signInError)}
              </Alert>
            )}

            {showLocalSignIn && (
              <Button
                variant='contained'
                size='large'
                fullWidth
                onClick={() => handleSignIn()}
                sx={{
                  // 8.1.3 — the sign-in button carries the Tenant's primary colour (hex stored without '#').
                  backgroundColor: `#${branding.primaryColor}`,
                  color: `#${branding.contrastTextColor}`,
                  '&:hover': {
                    backgroundColor: `#${branding.primaryColor}`,
                    filter: 'brightness(0.92)',
                  },
                }}
              >
                {formatMessage(messages.signInButton)}
              </Button>
            )}

            {/* E12 F12-7 — one actionable button per federation provider. Clicking routes to Entra with the
                provider's domain_hint (External ID has no native email-domain HRD, so routing is app-driven).
                A caution icon (F12-6) flags a provider with a domain awaiting verification. */}
            {federationProviders.map((provider) => (
              <Button
                key={provider.id}
                variant='outlined'
                size='large'
                fullWidth
                onClick={() => handleSignIn(provider.domainHint)}
                endIcon={
                  provider.hasUnverifiedDomain ? (
                    <Tooltip title={formatMessage(messages.providerUnverifiedCaution)}>
                      <WarningAmber fontSize='small' color='warning' />
                    </Tooltip>
                  ) : undefined
                }
              >
                {formatMessage(messages.signInWithProvider, { provider: provider.displayName })}
              </Button>
            ))}
          </Stack>
        </Paper>
      </Container>
    </Box>
  )
}

export default LoginPage
